import { AccountConfiguration } from "features/Accounts/Configuration/AccountConfiguration";
import { connect, type ConnectedProps } from "react-redux";
import { bindAction } from "interfaces";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useEffect } from "react";
import { fetchParentAccounts } from "features/Accounts/state/thunks/parentAccountThunk";
import { fetchContractTypes as fetchContractTypesAction } from "features/Accounts/state/thunks/contractTypesThunk";
import { fetchAccountTypes as fetchAccountTypesAction } from "features/Accounts/CreateAccount/state/thunks/createAccountThunk";
import { setAccountCreateConfiguration } from "features/Accounts/CreateAccount/state/slices/createAccountSlice";
import { fetchAccountsTags as fetchAccountsTagsAction } from "features/Accounts/state/thunks/accountsLabelsThunk";
import { setNewTag as setNewTagAction } from "features/Accounts/state/slices/tagsSlice";

type Props = {
  onIsValidChange: (isValid: boolean) => void;
  onDirtyChanged: (isDirty: boolean) => void;
  bindSubmitForm: (submitForm: () => void) => void;
  onParentAccountChange: (parentAccountId: number | null) => void;
  prefilled?: {
    name: string;
    logoUri: Nullable<string>;
  };
  onRemoveName?: () => void;
};

const CreateAccountConfiguration = ({
  accountId,
  accountTypes,
  bindSubmitForm,
  contractTypes,
  hasGlobalAccess,
  isLoadingAccountTypes,
  isLoadingContractTypes,
  isLoadingParentAccounts,
  isMobo,
  onDirtyChanged,
  onFetchParentAccounts,
  onIsValidChange,
  parentAccounts,
  onParentAccountChange,
  prefilled,
  onRemoveName,
  fetchContractTypes,
  fetchAccountTypes,
  setConfiguration,
  accountConfiguration,
  setNewTag,
  fetchAccountsTags,
  accountsLabels,
}: Props & PropsFromRedux) => {
  useEffect(() => {
    fetchContractTypes();
  }, [fetchContractTypes]);

  useEffect(() => {
    fetchAccountTypes();
  }, [fetchAccountTypes]);

  useEffect(() => {
    if (!accountsLabels.length) {
      fetchAccountsTags(false);
    }
  }, [accountsLabels.length, fetchAccountsTags]);

  return (
    <AccountConfiguration
      accountTypes={accountTypes}
      parentAccounts={parentAccounts}
      currentConfiguration={accountConfiguration}
      contractTypes={contractTypes}
      isLoadingAccountTypes={isLoadingAccountTypes}
      isLoadingParentAccounts={isLoadingParentAccounts}
      isLoadingContractTypes={isLoadingContractTypes}
      onIsValidChange={onIsValidChange}
      bindSubmitForm={bindSubmitForm}
      onSubmit={setConfiguration}
      onDirtyChanged={onDirtyChanged}
      accountId={accountId}
      isMobo={isMobo}
      hasGlobalAccess={hasGlobalAccess}
      onFetchParentAccounts={onFetchParentAccounts}
      prefilled={prefilled}
      onRemoveName={onRemoveName}
      onParentAccountChange={onParentAccountChange}
      accountsLabels={accountsLabels}
      onAddNewTag={setNewTag}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { creation, contractTypes, tags } = state.accounts;
  const { userProfile } = state;

  return {
    accountTypes: creation.accountTypes.items,
    parentAccounts: state.accounts.parentAccounts.items,
    contractTypes: contractTypes.items,
    isLoadingAccountTypes: creation.accountTypes.isLoading,
    isLoadingParentAccounts: state.accounts.parentAccounts.isLoading,
    isLoadingContractTypes: contractTypes.isLoading,
    accountId: userProfile.accountId,
    isMobo: userProfile.isMobo,
    hasGlobalAccess: userProfile.hasGlobalAccess,
    accountConfiguration: creation.configuration,
    accountsLabels: tags.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchAccountTypes: bindAction(fetchAccountTypesAction, dispatch),
    onFetchParentAccounts: bindAction(fetchParentAccounts, dispatch),
    fetchContractTypes: bindAction(fetchContractTypesAction, dispatch),
    setConfiguration: bindAction(setAccountCreateConfiguration, dispatch),
    fetchAccountsTags: bindAction(fetchAccountsTagsAction, dispatch),
    setNewTag: bindAction(setNewTagAction, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateAccountConfiguration);

export default ConnectedComponent;
